/**
 * The confidence step (#438 slice 2, HYP-UX-005; #660): before the first check, once per question of the sheet, one question at a
 * time - "How sure were you?" - Sure / Unsure / Guessed, nothing preselected, every one skippable, the learner's own page
 * beside it with that question's lines framed.
 *
 * What the answers are: the learner's own word about their own confidence, recorded as an observation by
 * `POST /homework/:id/confidence` and read by nothing yet. They are never shown back, never a status, a score or a count.
 * A tap keeps the choice in memory and moves to the next question; nothing is sent until the check is opened, as before, in one
 * request with every question, a group left blank as `skipped` (a skip is data: HYP-UX-005 is falsified by skipping).
 *
 * **No verdict is on this screen**: the page is drawn with no note, mark, chip or count, because the answer is asked before the
 * verdict so that "sure, then wrong" means something. The frames are only where a question's lines are (the readers' boxes), in
 * the course colour.
 */

import { useState } from 'react';
import type { HomeworkConfidenceRequest } from '@davinci/contracts';
import { useFeedbackLabels } from '../../i18n/homework-feedback-labels.js';
import { useMessages } from '../../i18n/locale.js';
import { feedbackChrome } from './feedback-chrome.js';
import { questionGroup, unionBbox } from './feedback-blocks.js';
import { BlockCrop } from './feedback-phone.js';
import { FeedbackPageImage, type Bbox } from './feedback-page-image.js';
import { PageViewer, type PageBox } from './feedback-viewer.js';
import { useIsPhone } from '../../app/use-is-phone.js';
import { UntrustedText } from './untrusted-text.js';

export type ConfidenceChoice = HomeworkConfidenceRequest['answers'][number]['answer'];
/** What the learner picked, keyed by question label; the sheet as a whole is keyed by the empty string (no label is empty). */
export type ConfidenceChoices = Readonly<Record<string, Exclude<ConfidenceChoice, 'skipped'>>>;

/** The request body: every question asked, an unanswered one as `skipped`. A null label is the sheet as a whole. */
export function confidenceAnswers(labels: readonly (string | null)[], choices: ConfidenceChoices): HomeworkConfidenceRequest['answers'] {
  return labels.map((questionLabel) => ({ questionLabel, answer: choices[questionLabel ?? ''] ?? 'skipped' }));
}

/** One page of the upload, with the lines the readers found on it (a question label and a box each). */
export interface ConfidencePage {
  readonly pageId: string;
  readonly pageNumber: number;
  readonly widthPx: number;
  readonly heightPx: number;
  readonly lines: readonly { readonly questionLabel: string | null; readonly bbox: Bbox }[];
}

/** The page to open on for a question, and the boxes to frame there: its first page with a line labelled so; the first page, unframed, when none. */
export function pageForQuestion(pages: readonly ConfidencePage[], label: string | null): { readonly page: ConfidencePage | undefined; readonly frames: readonly Bbox[] } {
  if (label !== null) {
    const found = pages.find((page) => page.lines.some((line) => line.questionLabel === label));
    if (found !== undefined) return { page: found, frames: found.lines.filter((line) => line.questionLabel === label).map((line) => line.bbox) };
  }
  return { page: pages[0], frames: [] };
}

function Sure({ label, onPick, onSkip }: { readonly label: string | null; readonly onPick: (choice: Exclude<ConfidenceChoice, 'skipped'>) => void; readonly onSkip: () => void }): React.JSX.Element {
  const t = useFeedbackLabels();
  const m = useMessages(feedbackChrome);
  const options: readonly { readonly value: Exclude<ConfidenceChoice, 'skipped'>; readonly label: string }[] = [
    { value: 'sure', label: t.confidenceSure },
    { value: 'not_sure', label: t.confidenceNotSure },
    { value: 'guessed', label: t.confidenceGuessed },
  ];
  return (
    <>
      <p id="fb-sure-question" className="fb-sure-question" aria-live="polite">
        {label === null ? m.sureWhole : (
          <>
            {m.sureQuestion} <UntrustedText text={label} />
          </>
        )}
      </p>
      <h2 id="fb-sure-heading">{m.sureHeading}</h2>
      <div className="fb-sure-rows" role="group" aria-labelledby="fb-sure-question fb-sure-heading">
        {options.map((option) => (
          <button key={option.value} type="button" className="fb-sure-row" onClick={() => onPick(option.value)}>
            <span aria-hidden="true" className="fb-sure-radio" />
            {option.label}
          </button>
        ))}
      </div>
      <button type="button" className="fb-sure-skip" onClick={onSkip}>
        {m.sureSkip}
      </button>
    </>
  );
}

/** The page on the question's lines, framed. Keyed by the step, so each question opens on its own page. */
function SureViewer({ pages, label, card, beside }: { readonly pages: readonly ConfidencePage[]; readonly label: string | null; readonly card: React.ReactNode; readonly beside: boolean }): React.JSX.Element | null {
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const start = pageForQuestion(pages, label);
  const page = pages.find((candidate) => candidate.pageId === picked) ?? start.page;
  if (page === undefined) return null;
  const at = pages.indexOf(page);
  return (
    <PageViewer
      current
      pageId={page.pageId}
      pageNumber={page.pageNumber}
      totalPages={pages.length}
      thumbs={pages.map((candidate) => ({ id: candidate.pageId, number: candidate.pageNumber }))}
      previousId={pages[at - 1]?.pageId}
      nextId={pages[at + 1]?.pageId}
      onGo={setPicked}
      widthPx={page.widthPx}
      heightPx={page.heightPx}
      {...(beside
        ? {
            // The card sits in the gutter, at the height of the question's first line.
            gutter: (box: PageBox) => {
              const top = page.pageId === start.page?.pageId && start.frames[0] !== undefined ? (start.frames[0].y0 / page.heightPx) * box.h : 0;
              return <div className="fb-sure-slot" style={{ paddingBlockStart: `${String(Math.max(0, top))}px` }}>{card}</div>;
            },
          }
        : {})}
    >
      <FeedbackPageImage
        key={page.pageId}
        pageId={page.pageId}
        pageNumber={page.pageNumber}
        widthPx={page.widthPx}
        heightPx={page.heightPx}
        lineFrames={page.pageId === start.page?.pageId ? start.frames : []}
        stripLabel={page.pageId === start.page?.pageId ? label : null}
      />
    </PageViewer>
  );
}


/** On a phone the question is the strip and the question's lines as one crop, whole (no viewer, no mark): what the card asks about. */
function SureCrop({ pages, label }: { readonly pages: readonly ConfidencePage[]; readonly label: string | null }): React.JSX.Element | null {
  const m = useMessages(feedbackChrome);
  if (label === null) return null;
  const { page, frames } = pageForQuestion(pages, label);
  const box = unionBbox(frames);
  return (
    <div className="fb-sure-phone">
      <p className="fb-phone-strip">
        <span className="fb-strip-label">{m.questionStrip(questionGroup(label) ?? label)}</span>
      </p>
      {page !== undefined && box !== null ? (
        <div className="fb-phone-block fb-sure-block">
          <div className="fb-phone-frame" data-kind="unchecked" data-outline="dashed">
            <BlockCrop pageId={page.pageId} bbox={box} lines={frames} lineNumber={1} widthPx={page.widthPx} heightPx={page.heightPx} text={undefined} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
/**
 * The step-by-step screen: the learner's pages, and a card that asks about one question and moves on at a tap. After the last
 * question the card is the caller's `done` (the line that says opening is recorded, the triage list, and the button that opens
 * the feedback).
 */
export function ConfidenceFlow(props: {
  readonly labels: readonly (string | null)[];
  readonly pages: readonly ConfidencePage[];
  readonly onChoose: (label: string | null, choice: Exclude<ConfidenceChoice, 'skipped'>) => void;
  readonly done: React.ReactNode;
}): React.JSX.Element {
  const m = useMessages(feedbackChrome);
  const [step, setStep] = useState(0);
  const asking = step < props.labels.length;
  const label = asking ? (props.labels[step] ?? null) : null;
  const next = (): void => setStep((current) => current + 1);
  const phone = useIsPhone();
  const card = (
        <section className="fb-sure-card" aria-labelledby={asking ? 'fb-sure-heading' : 'fb-sure-done'}>
          <span className="fb-dots" aria-hidden="true">
            {props.labels.map((_, index) => (
              <span key={index} className="fb-dot" data-filled={index <= step} />
            ))}
          </span>
          {asking ? (
            <Sure
              label={label}
              onPick={(choice) => {
                props.onChoose(label, choice);
                next();
              }}
              onSkip={next}
            />
          ) : (
            <>
              <h2 id="fb-sure-done">{m.sureAllAnswered}</h2>
              {props.done}
            </>
          )}
        </section>
  );
  return (
    <div className="fb-page fb-sure" data-current="true">
      {phone ? <SureCrop key={step} pages={props.pages} label={asking ? label : null} /> : <SureViewer key={step} pages={props.pages} label={asking ? label : null} card={card} beside />}
      {phone ? <div className="fb-notes">{card}</div> : null}
    </div>
  );
}
